/* Input park: the file input converts with the upload round (component-map.md). */
import { Box, Input, InputProps, Stack, useFormControl } from '@chakra-ui/react';
import { useEffectOnce } from '@main/shared/browser';
import {
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  PropsWithChildren,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { UploadFileCard, UploadFileCardProps } from '../cards';
import { errorToast } from '../toast';
import { FileInputContext, useFileInputContext } from './context';
import { Dropzone, DropzoneInput } from './dropzone';
import { filterFilesByAccept } from './file-accept';
import { FileUploadPreview } from './file-preview';
import { NhostFileUploader } from './nhost-upload-files';
import { UploadedFile } from './shared';

export type FileInputProps<T extends UploadedFile = UploadedFile> = Omit<
  FileInputContext<T>,
  'registerReuploadCb' | 'handleFileReupload'
>;

/**
 * Composable file upload component with context provider.
 *
 * Props should not be provided directly, and instead one of the following
 * supportive hooks should be used:
 * - useEagerFileUpload
 * - useEagerMultipleFilesUpload
 * - useLazyFileUpload
 * - useLazyMultipleFilesUpload
 *
 * @example
 * const fileUpload = useEagerFileUpload({ ... });
 *
 * <FileUpload {...fileUpload.props}>
 *   <FileUpload.Preview />
 *   <FileUpload.Files />
 *   <FileUpload.Dropzone />
 * </FileUpload>
 */
export const FileUpload = <T extends UploadedFile>({
  children,
  ...props
}: PropsWithChildren<FileInputProps<T>>) => {
  const reuploadCbs = useRef<Set<(file: T) => void>>(new Set());

  function registerReuploadCb(cb: (file: T) => void) {
    reuploadCbs.current.add(cb);
    return () => reuploadCbs.current.delete(cb);
  }

  function handleFileReupload(file: T) {
    reuploadCbs.current.forEach((cb) => cb(file));
  }

  const context: FileInputContext<T> = {
    ...props,
    registerReuploadCb,
    handleFileReupload,
  };

  return (
    <FileInputContext.Provider value={context}>
      <Stack w="full" spacing={3}>
        {children}
      </Stack>
    </FileInputContext.Provider>
  );
};

type FileUploadInputProps = Omit<InputProps, 'multiple' | 'onChange' | 'type'>;
const FileUploadInput = forwardRef(
  (props: FileUploadInputProps, forwardedRef: ForwardedRef<HTMLInputElement>) => {
    const {
      uploadedFiles,
      stagedFiles,
      isMulti,
      isReadOnly,
      onStagedFilesAdd,
      onUploadedFileReupload,
      registerReuploadCb,
    } = useFileInputContext();
    const { t } = useTranslation('ui');
    const readOnly = useFormControl({}).readOnly || isReadOnly;
    const reuploadRef = useRef<HTMLInputElement>();
    const reuploadingFile = useRef<UploadedFile>();

    const handleFiles = (files: File[], onAccepted: (acceptedFiles: File[]) => void) => {
      const { acceptedFiles, rejectedFiles, acceptedTypes } = filterFilesByAccept(
        files,
        props.accept,
      );

      if (rejectedFiles.length) {
        errorToast(
          t('upload.invalidFileType', {
            types: acceptedTypes.join(', '),
            interpolation: { escapeValue: false },
          }),
        );
      }

      if (acceptedFiles.length || !rejectedFiles.length) {
        onAccepted(acceptedFiles);
      }
    };

    useEffectOnce(() =>
      registerReuploadCb((file) => {
        reuploadingFile.current = file;
        reuploadRef.current?.click();
      }),
    );

    if (readOnly || (!isMulti && (uploadedFiles?.length || stagedFiles?.length))) {
      return (
        <Input
          type="file"
          style={{ display: 'none' }}
          {...props}
          ref={reuploadRef}
          onChange={(event) => {
            const uploadedFile = reuploadingFile.current;
            const selectedFiles = Array.from(event.target.files ?? []);
            handleFiles(selectedFiles, (acceptedFiles) => {
              if (
                acceptedFiles.length > 0 &&
                acceptedFiles.length === selectedFiles.length &&
                uploadedFile
              ) {
                void onUploadedFileReupload?.(uploadedFile, acceptedFiles);
              }
            });

            reuploadingFile.current = undefined;
            event.target.value = '';
          }}
        />
      );
    }

    return (
      <Input
        {...props}
        ref={forwardedRef}
        type="file"
        multiple={isMulti}
        onChange={(event) => {
          if (!event.target.files) {
            return;
          }
          const selectedFiles = Array.from(event.target.files);
          handleFiles(selectedFiles, (acceptedFiles) => {
            onStagedFilesAdd?.(acceptedFiles);
          });

          event.target.value = '';
        }}
      />
    );
  },
);
FileUpload.Input = FileUploadInput;

type FileInputDropzoneProps = {
  topLabelText?: string;
  bottomLabelText?: string;
  constraintText?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  onDragLeave?: () => void;
};
const FileInputDropzone = ({
  topLabelText,
  bottomLabelText,
  constraintText,
  inputProps,
  onDragLeave,
}: FileInputDropzoneProps) => {
  const {
    uploadedFiles,
    stagedFiles,
    isMulti,
    isReadOnly,
    onStagedFilesAdd,
    onUploadedFileReupload,
    registerReuploadCb,
  } = useFileInputContext();
  const { t } = useTranslation('ui');
  const readOnly = useFormControl({}).readOnly || isReadOnly;
  const reuploadRef = useRef<HTMLInputElement | null>(null);
  const reuploadingFile = useRef<UploadedFile>();

  const handleFiles = (files: File[], onAccepted: (acceptedFiles: File[]) => void) => {
    const { acceptedFiles, rejectedFiles, acceptedTypes } = filterFilesByAccept(
      files,
      inputProps?.accept,
    );

    if (rejectedFiles.length) {
      errorToast(
        t('upload.invalidFileType', {
          types: acceptedTypes.join(', '),
          interpolation: { escapeValue: false },
        }),
      );
    }

    if (acceptedFiles.length || !rejectedFiles.length) {
      onAccepted(acceptedFiles);
    }
  };

  useEffectOnce(() =>
    registerReuploadCb((file) => {
      reuploadingFile.current = file;
      reuploadRef.current?.click();
    }),
  );

  const hasFiles = !!(uploadedFiles?.length || stagedFiles?.length);
  if (readOnly || (!isMulti && hasFiles)) {
    /* The hidden re-upload input must not carry the field's id: the label
     * would open a file picker whose choice is thrown away. */
    return (
      <DropzoneInput
        style={{ display: 'none' }}
        {...inputProps}
        id={undefined}
        aria-labelledby={undefined}
        onChange={(event) => {
          const uploadedFile = reuploadingFile.current;
          const selectedFiles = Array.from(event.target.files ?? []);
          handleFiles(selectedFiles, (acceptedFiles) => {
            if (
              acceptedFiles.length > 0 &&
              acceptedFiles.length === selectedFiles.length &&
              uploadedFile
            ) {
              void onUploadedFileReupload?.(uploadedFile, acceptedFiles);
            }
          });

          reuploadingFile.current = undefined;
          event.target.value = '';
        }}
        ref={reuploadRef}
      />
    );
  }

  return (
    <Dropzone
      isMulti={isMulti}
      topLabelText={topLabelText}
      bottomLabelText={bottomLabelText}
      constraintText={constraintText}
      inputProps={inputProps}
      onChange={onStagedFilesAdd}
      onDragLeave={onDragLeave}
    />
  );
};

const FileUploadFiles = ({ maxSize }: { maxSize?: number }) => {
  const {
    uploadedFiles = [],
    stagedFiles = [],
    handleFileReupload,
    onUploadedFileDownload,
    onUploadedFileDelete,
    onUploadedFileReupload,
    onStagedFileDelete,
    icon,
    actions,
    getUploadedFileWarning,
    renderUploadedFile,
    isReadOnly,
  } = useFileInputContext();

  const [loadingFileIds, setLoadingFileIds] = useState<string[]>([]);

  async function handleFileDelete(file: UploadedFile) {
    setLoadingFileIds((prev) => [...prev, file.id]);
    try {
      await onUploadedFileDelete?.(file);
    } finally {
      setLoadingFileIds((prev) => prev.filter((id) => id !== file.id));
    }
  }

  return (
    <>
      {uploadedFiles.map((file) => {
        const cardProps: UploadFileCardProps & { file: UploadedFile } = {
          file,
          isUploaded: true,
          isReadOnly,
          isLoading: loadingFileIds.includes(file.id),
          onDownload: onUploadedFileDownload ? () => onUploadedFileDownload(file.id) : undefined,
          onReupload: onUploadedFileReupload ? () => handleFileReupload(file) : undefined,
          onDelete: onUploadedFileDelete ? () => handleFileDelete(file) : undefined,
          icon,
          actions,
          warning: getUploadedFileWarning?.(file),
        };
        return (
          <Box key={file.id} fontSize="md">
            {renderUploadedFile ? renderUploadedFile(cardProps) : <UploadFileCard {...cardProps} />}
          </Box>
        );
      })}
      {stagedFiles.map((file) => (
        <Box key={file.id} fontSize="md">
          <NhostFileUploader fileRef={file} onDelete={onStagedFileDelete} maxSize={maxSize} />
        </Box>
      ))}
    </>
  );
};

FileUpload.Dropzone = FileInputDropzone;
FileUpload.Preview = FileUploadPreview;
FileUpload.Files = FileUploadFiles;
